import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Provider';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const PROVIDER_API_REDUCER_KEY = 'providers';
export const PROVIDER_PATH = 'providers';

export const providerApi = createApi({
    reducerPath: PROVIDER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Provider', 'Providers'],
    endpoints: (builder) => ({
        getProviderList: builder.query<PaginationResult<Entity.Provider>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ PROVIDER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Providers'],
        }),
        getProvider: builder.query<Entity.Provider, Entity.PrimaryProvider>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = providerNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ PROVIDER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Provider'],
        }),
        getProviderMultiple: builder.query<Entity.Provider, Entity.PrimaryProvider[]>({
            query: (ids) => {
                return ({
                    url: `/${ PROVIDER_PATH }/ids${Entity.PrimaryProvider.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Providers'],
        }),
        getProviderPresentation: builder.query<PaginationResult<Entity.Provider>, Entity.PresentationProviderRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ PROVIDER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Providers'],
        }),
        
        insertProvider: builder.mutation<Entity.Provider, Entity.CreateProvider>({
            query: (toInsert) => {
                return ({
                    url: `/${ PROVIDER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    providerApi.util.upsertQueryData('getProvider', Entity.PrimaryProvider.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Providers'],
        }),
        insertMultipleProvider: builder.mutation<Entity.Provider[], Entity.CreateProvider[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ PROVIDER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            providerApi.util.upsertQueryData('getProvider', Entity.PrimaryProvider.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Providers'],
        }),
        updateProvider: builder.mutation<Entity.Provider, Entity.UpdateEntityProvider>({
            query: (toUpdate) => {
                return ({
                    url: `/${ PROVIDER_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Providers'],
        }),
        updateMultipleProvider: builder.mutation<Entity.Provider[], Entity.UpdateEntityProvider[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ PROVIDER_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Providers'],
        }),
        deleteProvider: builder.mutation<Entity.Provider, Entity.Provider>({
            query: (toDelete) => {
                return ({
                    url: `/${ PROVIDER_PATH }/${Entity.PrimaryProvider.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Providers'],
        }),
        deleteMultipleProvider: builder.mutation<Entity.Provider[], Entity.PrimaryProvider[]>({
            query: (ids) => {
                return ({
                    url: `/${ PROVIDER_PATH }/batch${Entity.PrimaryProvider.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Providers'],
        }),
    }),
})

export const [providerNormalizer, providerNormalizerMiddleware] = createNormalizationMiddleware(PROVIDER_API_REDUCER_KEY, providerApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetProviderListQuery,
    useGetProviderMultipleQuery,
    useGetProviderPresentationQuery,
    useGetProviderQuery,
    useInsertProviderMutation,
    useInsertMultipleProviderMutation,
    useUpdateProviderMutation,
    useUpdateMultipleProviderMutation,
    useDeleteProviderMutation,
    useDeleteMultipleProviderMutation,
    
} = providerApi
